import React from 'react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { DependenciesIcon } from '../../components_misc/DependenciesIcon';

export type DependencyIconState = 'all-visible' | 'none-visible' | 'mixed' | 'no-dependencies';

interface DualIconContainerProps {
  // Visibility icon state
  isMapVisible: boolean;
  onVisibilityToggle: () => void;

  // Dependencies icon state
  dependencyState: DependencyIconState;
  onDependencyToggle: () => void;

  // Accessibility
  mapTitle: string;
  dependencyCount?: number;
  visibleDependencyCount?: number;

  // Styling
  className?: string;
  disabled?: boolean;
}

export function DualIconContainer({
  isMapVisible,
  onVisibilityToggle,
  dependencyState,
  onDependencyToggle,
  mapTitle,
  dependencyCount = 0,
  visibleDependencyCount = 0,
  className = '',
  disabled = false,
}: DualIconContainerProps) {
  // Determine which eye icon to show
  const EyeComponent = isMapVisible ? EyeIcon : EyeSlashIcon;

  // Generate accessibility labels
  const visibilityLabel = `${isMapVisible ? 'Hide' : 'Show'} map: ${mapTitle}`;
  const dependencyLabel =
    dependencyCount === 0
      ? `No dependencies for ${mapTitle}`
      : `Toggle ${dependencyCount} dependencies for ${mapTitle} (${visibleDependencyCount} visible)`;

  // Clean dependency icon styling based on state
  const getDependencyIconStyling = () => {
    switch (dependencyState) {
      case 'all-visible':
        return 'text-slate-800 border-2 border-slate-800 hover:text-slate-900 bg-slate-300 hover:bg-slate-300'; // All visible - thick border, darkest styling
      case 'mixed':
        return 'text-slate-600 border border-slate-600 hover:text-slate-700 bg-slate-200 hover:bg-slate-200'; // Mixed state - thin border, medium styling
      case 'none-visible':
        return 'text-slate-400 border border-transparent hover:text-slate-500 bg-transparent hover:bg-slate-50'; // None visible - light icon, no visible border
      case 'no-dependencies':
        return 'text-gray-300 border border-transparent cursor-not-allowed bg-transparent'; // No dependencies - disabled
      default:
        return 'text-slate-600 border border-slate-600 hover:text-slate-700 bg-transparent hover:bg-slate-50';
    }
  };

  // Simple visual variant selection for MVP
  const getIconVariant = () => {
    return dependencyState === 'all-visible' ? 'filled' : 'outline';
  };

  return (
    <div className={`inline-flex items-center gap-2 ${className}`}>
      {/* Visibility Toggle Icon */}
      <button
        type="button"
        onClick={onVisibilityToggle}
        disabled={disabled}
        className={`
          inline-flex items-center justify-center
          w-5 h-5 min-w-[20px] min-h-[20px]
          transition-colors duration-150
          focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-1
          rounded-sm
          text-slate-600 hover:text-slate-700
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
        aria-label={visibilityLabel}
        title={visibilityLabel}
      >
        <EyeComponent className="w-4 h-4" />
      </button>

      {/* Dependencies Toggle Icon */}
      <button
        type="button"
        onClick={onDependencyToggle}
        disabled={disabled || dependencyState === 'no-dependencies'}
        className={`
          inline-flex items-center justify-center
          w-5 h-5 min-w-[20px] min-h-[20px]
          transition-colors duration-150
          focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-1
          rounded-sm
          ${getDependencyIconStyling()}
          ${
            disabled || dependencyState === 'no-dependencies'
              ? 'opacity-50 cursor-not-allowed'
              : 'cursor-pointer'
          }
        `}
        aria-label={dependencyLabel}
        title={dependencyLabel}
      >
        <DependenciesIcon className="w-4 h-4" variant={getIconVariant()} />
      </button>
    </div>
  );
}
